import type { FunctionComponent } from 'react';
import { Suspense, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  ChunkLoadErrorBoundary,
  ErrorBoundary,
} from '@trello/error-boundaries';
import { HeaderMenu } from '@trello/header-primitives';
import { intl } from '@trello/i18n';
import { usePopover } from '@trello/nachos/popover';
import type { WorkspaceSwitcherTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { useLazyComponent } from '@trello/use-lazy-component';

import type { InjectedHeaderComponents } from 'app/src/components/Header/InjectedHeaderComponents.types';

const ErrorComponent: FunctionComponent = () => (
  <p>
    <FormattedMessage
      id="templates.workspace_switcher.error-loading-teams"
      defaultMessage="There was an error loading your Workspaces."
      description="An error occurred while loading workspaces."
    />
  </p>
);

type WorkspaceSwitcherProps = Pick<
  InjectedHeaderComponents,
  'InviteTeamMembersForm'
>;

export const WorkspaceSwitcher: FunctionComponent<WorkspaceSwitcherProps> = ({
  InviteTeamMembersForm,
}) => {
  const [shouldHidePopover, setShouldHidePopover] = useState(false);

  const { popoverProps } = usePopover<HTMLAnchorElement>();

  const CreateWorkspaceOverlay = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-team-overlay" */ 'app/src/components/CreateTeamOverlay'
      ),

    {
      preload: false,
      namedImport: 'CreateTeamOverlay',
    },
  );

  const WorkspaceSwitcherPopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "workspace-switcher-popover" */ './WorkspaceSwitcherPopover'
      ),

    {
      preload: popoverProps.isVisible,
      namedImport: 'WorkspaceSwitcherPopover',
    },
  );

  const [showCreateWorkspaceOverlay, setShowCreateWorkspaceOverlay] =
    useState(false);

  const closeOverlay = useCallback(() => {
    setShowCreateWorkspaceOverlay(false);
  }, [setShowCreateWorkspaceOverlay]);

  const onClickCreateTeam = useCallback(() => {
    setShowCreateWorkspaceOverlay(true);
    setShouldHidePopover(true);
  }, [setShowCreateWorkspaceOverlay, setShouldHidePopover]);

  const onWorkspaceClick = useCallback(() => {
    setShouldHidePopover(true);
  }, [setShouldHidePopover]);

  const resetShouldHidePopover = useCallback(() => {
    setShouldHidePopover(false);
  }, [setShouldHidePopover]);

  return (
    <>
      <HeaderMenu
        buttonText={intl.formatMessage({
          id: 'templates.workspace_switcher.teams',
          defaultMessage: 'Workspaces',
          description: 'Workspaces',
        })}
        analyticsButtonName="workspaceSwitcherButton"
        analyticsComponentName="workspaceSwitcherMenuInlineDialog"
        ariaPopoverTitle={intl.formatMessage({
          id: 'templates.workspace_switcher.teams',
          defaultMessage: 'Workspaces',
          description: 'Workspaces',
        })}
        dataTestId={getTestId<WorkspaceSwitcherTestIds>('workspace-switcher')}
        shouldHidePopover={shouldHidePopover}
        resetShouldHidePopover={resetShouldHidePopover}
        noVerticalPadding
      >
        <ErrorBoundary
          tags={{
            ownershipArea: 'trello-web-eng',
            feature: 'Workspace Switcher',
          }}
          errorHandlerComponent={ErrorComponent}
        >
          <ChunkLoadErrorBoundary fallback={<ErrorComponent />}>
            <Suspense fallback={null}>
              <WorkspaceSwitcherPopover
                onCreateTeamOverlayOpen={onClickCreateTeam}
                onWorkspaceClick={onWorkspaceClick}
              />
            </Suspense>
          </ChunkLoadErrorBoundary>
        </ErrorBoundary>
      </HeaderMenu>
      {showCreateWorkspaceOverlay && (
        <ChunkLoadErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <CreateWorkspaceOverlay
              teamType="default"
              onClose={closeOverlay}
              InviteTeamMembersForm={InviteTeamMembersForm}
            />
          </Suspense>
        </ChunkLoadErrorBoundary>
      )}
    </>
  );
};
